import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const OrganizerLogin = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(formData, 'organizer');
      
      if (result.success) {
        // Small delay to ensure state is fully updated
        setTimeout(() => {
          navigate('/organizer/dashboard');
        }, 100);
      }
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-yellow-400 rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
          <h2 className="text-4xl text-gray-900 font-extrabold text-white mb-2">
            Organizer Portal
          </h2>
          <p className="text-yellow-400 font-weight-bold text-lg">Sign in to manage your events</p>
        </div>

        {/* Login Card */}
        <div className="card bg-white/10 backdrop-blur-lg p-4 border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Address
                </span>
              </label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                className="w-full px-4 py-3 bg-white text-gray-900 border-2 border-gray-900 outline-none rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 placeholder-gray-500"
                placeholder="organizer@gatepass.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Password
                </span>
              </label>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                required
                className="w-full px-4 py-3 bg-white text-gray-900 border-2 border-gray-900 outline-none rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 placeholder-gray-500"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-400 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-yellow-500 hover:to-yellow-500 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In as Organizer'
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link to="/organizer/register" className="block text-sm text-yellow-400 hover:text-yellow-400 transition-colors">
              Don't have an account? Register as Organizer
            </Link>
            <Link to="/" className="block text-sm text-gray-600 hover:text-white transition-colors">
              ← Back to Home
            </Link>
          </div>
        </div>

        <div className="text-center text-sm text-gray-400">
          <p>Demo credentials: organizer@gatepass.com / organizer123</p>
        </div>
      </div>
    </div>
  );
};

export default OrganizerLogin;

